/* eslint-disable react/prop-types */
import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import ReactPlayer from 'react-player';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import Text from '../../../../shared/components/Text/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Spinner from '../../../../shared/components/Spinner/Spinner';
import { uploadMedia } from '../../ducks/calendarActions';
import { showErrorToast } from '../../../../shared/utils/toastManager';

const IconAndTextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`;

const DragAndDropContainer = styled('div')`
  border: 1px dashed #77A7FF;
  cursor: pointer;
  background-color: ${(props) => (props.isDragActive ? 'white' : '#FCFCFC')};
  opacity: ${(props) => (props.isDragActive ? 0.7 : 1)};
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  transition: ${TransitionGlobal};
  :hover {
    opacity: 0.7;
  }
`;

const VideoContainer = styled('div')`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  position: relative;
  border: ${(props) => props.isMediaLoading && '1px solid #EEEEEE'};
  video {
    border: 1px solid #EEEEEE;
    border-radius: 4px;
    object-fit: cover;
  }
  .react-player__preview {
    border: 1px solid #EEEEEE;
    border-radius: 4px;
  }
`;

const PlayButton = styled('div')`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  border: 3px solid white;
`;

const DragDropVideoEmbed = ({ mediaUrlValue, setFieldValue }) => {
  const dispatch = useDispatch();
  const [isMediaLoading, setIsMediaLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const uploadedMedia = useSelector((state) => state.calendar.data.uploadedMedia);

  useEffect(() => {
    /** if uploadedMedia already exists and thumbnail url changes update the displayed thumbnail */
    if (uploadedMedia) {
      setThumbnailUrl(uploadedMedia.thumbnailUrl);
    }
  }, [uploadedMedia?.thumbnailUrl]);

  const onDrop = useCallback((acceptedFiles) => {
    console.log('dropped file:', acceptedFiles);
    const file = acceptedFiles[0];
    if (file && file.type.includes('video')) {
      console.log('file:', file);
      /** Hit our API for upload media */
      setIsMediaLoading(true);
      dispatch(uploadMedia(accountCode, file, 'video', {
        onSuccess: (res) => {
          setFieldValue('mediaUrl', res.mediaUrl);
          setThumbnailUrl(res.thumbnailUrl);
          setIsMediaLoading(false);
          setFieldValue('mediaId', res.id);
        },
        onError: (err) => {
          showErrorToast(err);
          setIsMediaLoading(false);
        },
      }));
    } else {
      showErrorToast('Please drop a valid video file.');
      setIsMediaLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'video/*',
  });

  return (
    <>
      {isMediaLoading ? (
        <VideoContainer isMediaLoading={isMediaLoading}>
          <Spinner />
        </VideoContainer>
      ) : (
        <>
          {mediaUrlValue ? (
            <VideoContainer onClick={() => setIsPlaying(true)}>
              <ReactPlayer
                url={mediaUrlValue}
                light={!isPlaying ? thumbnailUrl : false} /** Show thumbnail when not playing */
                playing={isPlaying}
                controls
                playIcon={(
                  <PlayButton>
                    <IcomoonReact
                      iconSet={iconSet}
                      size={20}
                      icon='play'
                      color='white'
                      style={{ marginLeft: '2px' }}
                    />
                  </PlayButton>
                )}
                width='100%'
                height='250px'
                onPlay={() => setIsPlaying(true)}
                onEnded={() => setIsPlaying(false)}
                style={{ objectFit: 'cover' }}
              />
            </VideoContainer>
          ) : (
            <DragAndDropContainer
              isDragActive={isDragActive}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              <IconAndTextContainer>
                <IcomoonReact
                  iconSet={iconSet}
                  size={30}
                  icon='download'
                  color='#77A7FF'
                  style={{ transform: 'rotate(180deg)' }}
                />
                <Text fontSize='16px' fontWeight={700} color='#77A7FF'>Click or drag video here.</Text>
              </IconAndTextContainer>
            </DragAndDropContainer>
          )}
        </>
      )}
    </>
  );
};

export default DragDropVideoEmbed;
